import React, { useState } from 'react';
import type { AgentApproval } from '../../types/agent';
import { agentApi, apiErrorMessage } from '../../services/agentApi';
import { btn, fmtDateTime, input } from './agentUi';

const FIELD_LABELS: Record<string, string> = {
  customerName: 'Customer',
  newCustomerName: 'New customer',
  projectName: 'Folder',
  documentCount: 'Documents',
  displayName: 'Workflow',
  name: 'Name',
  newName: 'New name',
  oldName: 'Current name',
  content: 'Memory',
  to: 'To',
  subject: 'Subject',
  formNumber: 'ACORD form',
  kind: 'Kind',
};

function summarizeArgs(args: Record<string, any>): Array<[string, string]> {
  const rows: Array<[string, string]> = [];
  for (const [k, label] of Object.entries(FIELD_LABELS)) {
    const v = args[k];
    if (v === undefined || v === null || v === '') continue;
    rows.push([label, Array.isArray(v) ? v.join(', ') : String(v)]);
  }
  if (args.thenRun?.displayName) rows.push(['Then run', args.thenRun.displayName]);
  if (Array.isArray(args.documents)) rows.push(['Documents', args.documents.map((d: any) => d.name).join(', ')]);
  return rows;
}

/** Before → after for edit_customer; key fields for everything else. */
const ApprovalCard: React.FC<{ approval: AgentApproval; canDecide: boolean; onResolved: () => void; showRun?: boolean }> = ({ approval, canDecide, onResolved, showRun }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const args = approval.action.args || {};
  const shadow = approval.reason === 'shadow';

  const decide = async (decision: 'approve' | 'reject') => {
    setBusy(decision);
    setError(null);
    try {
      if (decision === 'approve') {
        const r = await agentApi.approve(approval.id, note || undefined);
        if (r.executionResult?.error) setError(`Approved, but it failed: ${r.executionResult.error}`);
      } else {
        await agentApi.reject(approval.id, note || undefined);
      }
      onResolved();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not record the decision'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={`border rounded-lg p-3 ${shadow ? 'border-purple-200 bg-purple-50/50 dark:border-purple-800 dark:bg-purple-900/10' : 'border-amber-200 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-900/10'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-white">{approval.action.displayName || approval.action.tool}</div>
          <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
            {shadow ? 'Proposed in shadow mode — approving runs it now.' : approval.reason}
          </div>
          {showRun && approval.run?.email?.subject && <div className="text-xs text-gray-500 mt-0.5">From: “{approval.run.email.subject}”</div>}
        </div>
        <span className="text-[11px] text-gray-500 flex-shrink-0">{fmtDateTime(approval.requestedAt)}</span>
      </div>

      {approval.action.tool === 'edit_customer' && args.before ? (
        <table className="mt-2 text-xs w-full">
          <tbody>
            {Object.keys(args.after || {}).filter((k) => args.after[k] !== undefined).map((k) => (
              <tr key={k}>
                <td className="pr-2 text-gray-500 capitalize">{k}</td>
                <td className="pr-2 line-through text-gray-500">{String(args.before[k] ?? '—')}</td>
                <td className="font-medium text-gray-900 dark:text-white">→ {String(args.after[k])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-xs">
          {summarizeArgs(args).map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="text-gray-500">{k}</dt>
              <dd className="text-gray-900 dark:text-gray-100 break-words">{v}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}

      {canDecide && approval.status === 'pending' && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input className={`${input} flex-1 min-w-[160px]`} placeholder="Note (optional — Arie sees it)" value={note} onChange={(e) => setNote(e.target.value)} />
          <button className={btn.success} onClick={() => decide('approve')} disabled={!!busy}>{busy === 'approve' ? 'Approving…' : 'Approve'}</button>
          <button className={btn.secondary} onClick={() => decide('reject')} disabled={!!busy}>{busy === 'reject' ? 'Rejecting…' : 'Reject'}</button>
        </div>
      )}
      {error && <p className="text-xs text-red-600 dark:text-red-400 mt-2">{error}</p>}
    </div>
  );
};

export default ApprovalCard;
